import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { 
  Archive, 
  Trash2, 
  Database, 
  Calendar, 
  AlertTriangle, 
  CheckCircle,
  Loader2,
  FileText,
  Users,
  ClipboardList,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  GraduationCap,
  UserCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArchiveManagementPage = () => {
  const [summary, setSummary] = useState(null);
  const [archivedData, setArchivedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [cutoffDate, setCutoffDate] = useState('');
  const [includeUsers, setIncludeUsers] = useState(false);
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    examResults: false,
    attendanceSessions: false,
    attendanceRecords: false,
    enrollments: false,
    users: false
  });
  
  // Dialog states
  const [showArchiveExams, setShowArchiveExams] = useState(false);
  const [showArchiveAttendance, setShowArchiveAttendance] = useState(false);
  const [showArchiveRegistrations, setShowArchiveRegistrations] = useState(false);
  const [showClearAll, setShowClearAll] = useState(false);
  const [showPermanentDelete, setShowPermanentDelete] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/summary`);
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching archive summary:', error);
      toast.error('Failed to fetch archive summary');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArchivedData = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/history`);
      if (response.ok) {
        const data = await response.json();
        setArchivedData(data);
      }
    } catch (error) {
      console.error('Error fetching archived data:', error);
      toast.error('Failed to fetch archived data');
    } finally {
      setLoadingArchived(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchArchivedData();
  }, [fetchSummary, fetchArchivedData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const handleArchiveExamResults = async (archiveAll = false) => {
    if (!archiveAll && !cutoffDate) {
      toast.error('Please select a cutoff date');
      return;
    }

    setArchiving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/exam-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cutoff_date: archiveAll ? null : cutoffDate,
          archive_all: archiveAll
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to archive exam results');
      }

      toast.success(data.message);
      setShowArchiveExams(false);
      setCutoffDate('');
      fetchSummary();
      fetchArchivedData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setArchiving(false);
    }
  };

  const handleArchiveAttendance = async (archiveAll = false) => {
    if (!archiveAll && !cutoffDate) {
      toast.error('Please select a cutoff date');
      return;
    }

    setArchiving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cutoff_date: archiveAll ? null : cutoffDate,
          archive_all: archiveAll
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to archive attendance');
      }

      toast.success(data.message);
      setShowArchiveAttendance(false);
      setCutoffDate('');
      fetchSummary();
      fetchArchivedData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setArchiving(false);
    }
  };

  const handleArchiveRegistrations = async (archiveAll = false) => {
    if (!archiveAll && !cutoffDate) {
      toast.error('Please select a cutoff date');
      return;
    }

    setArchiving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cutoff_date: archiveAll ? null : cutoffDate,
          archive_all: archiveAll,
          include_users: includeUsers
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to archive registrations');
      }

      toast.success(data.message);
      setShowArchiveRegistrations(false);
      setCutoffDate('');
      setIncludeUsers(false);
      fetchSummary();
      fetchArchivedData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setArchiving(false);
    }
  };

  const handleClearAll = async () => {
    setArchiving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/clear-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to clear data');
      }

      toast.success(data.message);
      setShowClearAll(false);
      fetchSummary();
      fetchArchivedData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setArchiving(false);
    }
  };

  const handlePermanentDelete = async () => {
    setArchiving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/archive/permanent-delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true, type: 'all' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete archived data');
      }

      toast.success(data.message);
      setShowPermanentDelete(false);
      fetchSummary();
      fetchArchivedData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <AdminProtected>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Archive className="text-blue-600" />
                Data Archive Management
              </h1>
              <p className="text-gray-600 mt-2">
                Archive old data to keep your system fresh while preserving historical records
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {/* Current Data Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Exam Results</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {summary?.current_data?.exam_results || 0}
                          </p>
                        </div>
                        <FileText className="h-8 w-8 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Attendance Sessions</p>
                          <p className="text-2xl font-bold text-green-600">
                            {summary?.current_data?.attendance_sessions || 0}
                          </p>
                        </div>
                        <ClipboardList className="h-8 w-8 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Attendance Records</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {summary?.current_data?.attendance_records || 0}
                          </p>
                        </div>
                        <ClipboardList className="h-8 w-8 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Enrollments</p>
                          <p className="text-2xl font-bold text-orange-600">
                            {summary?.current_data?.enrollments || 0}
                          </p>
                        </div>
                        <Database className="h-8 w-8 text-orange-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Registered Users</p>
                          <p className="text-2xl font-bold text-teal-600">
                            {summary?.current_data?.users || 0}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-teal-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Date Range Info */}
                {summary?.date_range?.oldest_exam && (
                  <Card className="mb-8">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-4">
                        <Calendar className="h-5 w-5 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-500">Data Range</p>
                          <p className="font-medium">
                            {new Date(summary.date_range.oldest_exam).toLocaleDateString()} 
                            {' — '} 
                            {new Date(summary.date_range.newest_exam).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Archive Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="text-blue-600" />
                        Archive Exam Results
                      </CardTitle>
                      <CardDescription>
                        Move old exam results to archive storage
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        onClick={() => setShowArchiveExams(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="archive-exams-btn"
                      >
                        <Archive size={16} className="mr-2" />
                        Archive Exam Results
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardList className="text-green-600" />
                        Archive Attendance
                      </CardTitle>
                      <CardDescription>
                        Move old attendance sessions and records to archive
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        onClick={() => setShowArchiveAttendance(true)}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="archive-attendance-btn"
                      >
                        <Archive size={16} className="mr-2" />
                        Archive Attendance
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="text-orange-600" />
                        Archive Registrations
                      </CardTitle>
                      <CardDescription>
                        Move old user enrollments and accounts to archive
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        onClick={() => setShowArchiveRegistrations(true)}
                        className="w-full bg-orange-600 hover:bg-orange-700"
                        data-testid="archive-registrations-btn"
                      >
                        <Archive size={16} className="mr-2" />
                        Archive Registrations
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Archived Data Summary with Expandable Details */}
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="text-gray-600" />
                      Archived Data
                      {loadingArchived && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
                    </CardTitle>
                    <CardDescription>
                      Click on each section to view archived records
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Archived Exam Results */}
                    <div className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('examResults')}
                        className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 transition-colors"
                        data-testid="toggle-exam-results"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="text-blue-600" />
                          <span className="font-medium">Archived Exam Results</span>
                          <Badge variant="secondary">{summary?.archived_data?.exam_results || 0}</Badge>
                        </div>
                        {expandedSections.examResults ? <ChevronUp /> : <ChevronDown />}
                      </button>
                      {expandedSections.examResults && (
                        <div className="p-4 bg-white max-h-80 overflow-y-auto">
                          {archivedData?.archived_exam_results?.data?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2">Student</th>
                                  <th className="text-left p-2">School</th>
                                  <th className="text-left p-2">Exam Type</th>
                                  <th className="text-left p-2">Score</th>
                                  <th className="text-left p-2">Status</th>
                                  <th className="text-left p-2">Archived At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {archivedData.archived_exam_results.data.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2">{item.user_name || item.user_email || 'N/A'}</td>
                                    <td className="p-2">{item.school_id}</td>
                                    <td className="p-2">{item.exam_type || 'mock'}</td>
                                    <td className="p-2">{item.score}%</td>
                                    <td className="p-2">
                                      <Badge variant={item.passed ? "success" : "destructive"}>
                                        {item.passed ? 'Passed' : 'Failed'}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-gray-500">{item.archived_at_est || formatDate(item.archived_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No archived exam results</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Archived Attendance Sessions */}
                    <div className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('attendanceSessions')}
                        className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
                        data-testid="toggle-attendance-sessions"
                      >
                        <div className="flex items-center gap-3">
                          <ClipboardList className="text-green-600" />
                          <span className="font-medium">Archived Attendance Sessions</span>
                          <Badge variant="secondary">{summary?.archived_data?.attendance_sessions || 0}</Badge>
                        </div>
                        {expandedSections.attendanceSessions ? <ChevronUp /> : <ChevronDown />}
                      </button>
                      {expandedSections.attendanceSessions && (
                        <div className="p-4 bg-white max-h-80 overflow-y-auto">
                          {archivedData?.archived_attendance_sessions?.data?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2">School</th>
                                  <th className="text-left p-2">Date</th>
                                  <th className="text-left p-2">Topic</th>
                                  <th className="text-left p-2">Archived At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {archivedData.archived_attendance_sessions.data.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2">{item.school_id}</td>
                                    <td className="p-2">{item.session_date}</td>
                                    <td className="p-2">{item.topic || 'N/A'}</td>
                                    <td className="p-2 text-gray-500">{item.archived_at_est || formatDate(item.archived_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No archived attendance sessions</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Archived Attendance Records */}
                    <div className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('attendanceRecords')}
                        className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
                        data-testid="toggle-attendance-records"
                      >
                        <div className="flex items-center gap-3">
                          <UserCheck className="text-purple-600" />
                          <span className="font-medium">Archived Attendance Records</span>
                          <Badge variant="secondary">{summary?.archived_data?.attendance_records || 0}</Badge>
                        </div>
                        {expandedSections.attendanceRecords ? <ChevronUp /> : <ChevronDown />}
                      </button>
                      {expandedSections.attendanceRecords && (
                        <div className="p-4 bg-white max-h-80 overflow-y-auto">
                          {archivedData?.archived_attendance_records?.data?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2">Student Name</th>
                                  <th className="text-left p-2">Status</th>
                                  <th className="text-left p-2">Session ID</th>
                                  <th className="text-left p-2">Archived At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {archivedData.archived_attendance_records.data.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2">{item.student_name || 'N/A'}</td>
                                    <td className="p-2">
                                      <Badge variant={item.status === 'present' ? 'success' : item.status === 'absent' ? 'destructive' : 'secondary'}>
                                        {item.status}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-xs text-gray-500">{item.session_id}</td>
                                    <td className="p-2 text-gray-500">{item.archived_at_est || formatDate(item.archived_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No archived attendance records</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Archived Enrollments */}
                    <div className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('enrollments')}
                        className="w-full flex items-center justify-between p-4 bg-orange-50 hover:bg-orange-100 transition-colors"
                        data-testid="toggle-enrollments"
                      >
                        <div className="flex items-center gap-3">
                          <GraduationCap className="text-orange-600" />
                          <span className="font-medium">Archived Enrollments</span>
                          <Badge variant="secondary">{summary?.archived_data?.enrollments || 0}</Badge>
                        </div>
                        {expandedSections.enrollments ? <ChevronUp /> : <ChevronDown />}
                      </button>
                      {expandedSections.enrollments && (
                        <div className="p-4 bg-white max-h-80 overflow-y-auto">
                          {archivedData?.archived_enrollments?.data?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2">User ID</th>
                                  <th className="text-left p-2">School</th>
                                  <th className="text-left p-2">Enrolled At</th>
                                  <th className="text-left p-2">Archived At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {archivedData.archived_enrollments.data.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2 text-xs">{item.user_id?.substring(0, 12)}...</td>
                                    <td className="p-2">{item.school_id}</td>
                                    <td className="p-2">{formatDate(item.enrolled_at)}</td>
                                    <td className="p-2 text-gray-500">{item.archived_at_est || formatDate(item.archived_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No archived enrollments</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Archived Users */}
                    <div className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('users')}
                        className="w-full flex items-center justify-between p-4 bg-teal-50 hover:bg-teal-100 transition-colors"
                        data-testid="toggle-users"
                      >
                        <div className="flex items-center gap-3">
                          <Users className="text-teal-600" />
                          <span className="font-medium">Archived Users</span>
                          <Badge variant="secondary">{summary?.archived_data?.users || 0}</Badge>
                        </div>
                        {expandedSections.users ? <ChevronUp /> : <ChevronDown />}
                      </button>
                      {expandedSections.users && (
                        <div className="p-4 bg-white max-h-80 overflow-y-auto">
                          {archivedData?.archived_users?.data?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-2">Name</th>
                                  <th className="text-left p-2">Email</th>
                                  <th className="text-left p-2">Branch</th>
                                  <th className="text-left p-2">Phone</th>
                                  <th className="text-left p-2">Archived At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {archivedData.archived_users.data.map((item, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="p-2">{item.name}</td>
                                    <td className="p-2">{item.email}</td>
                                    <td className="p-2">{item.branch || 'N/A'}</td>
                                    <td className="p-2">{item.phone || 'N/A'}</td>
                                    <td className="p-2 text-gray-500">{item.archived_at_est || formatDate(item.archived_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No archived users</p>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Danger Zone */}
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle />
                      Danger Zone
                    </CardTitle>
                    <CardDescription className="text-red-600">
                      These actions are irreversible. Proceed with caution.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button 
                        variant="outline"
                        className="border-orange-500 text-orange-600 hover:bg-orange-50"
                        onClick={() => setShowClearAll(true)}
                        data-testid="clear-all-btn"
                      >
                        <RefreshCw size={16} className="mr-2" />
                        Archive & Clear All Data
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-100"
                        onClick={() => setShowPermanentDelete(true)}
                        data-testid="permanent-delete-btn"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Permanently Delete Archived Data
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </main>

        <Footer />

        {/* Archive Exam Results Dialog */}
        <Dialog open={showArchiveExams} onOpenChange={setShowArchiveExams}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="text-blue-600" />
                Archive Exam Results
              </DialogTitle>
              <DialogDescription>
                Move exam results to archive storage. You can archive by date or archive all.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="cutoff_date">Archive results before this date:</Label>
                <Input
                  id="cutoff_date"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => handleArchiveExamResults(false)}
                  disabled={archiving || !cutoffDate}
                  className="flex-1"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Archive by Date
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleArchiveExamResults(true)}
                  disabled={archiving}
                  className="flex-1"
                >
                  Archive All
                </Button>
              </div>
              
              <Button variant="ghost" onClick={() => setShowArchiveExams(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Archive Attendance Dialog */}
        <Dialog open={showArchiveAttendance} onOpenChange={setShowArchiveAttendance}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="text-green-600" />
                Archive Attendance Data
              </DialogTitle>
              <DialogDescription>
                Move attendance sessions and records to archive storage.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="attendance_cutoff">Archive sessions before this date:</Label>
                <Input
                  id="attendance_cutoff"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => handleArchiveAttendance(false)}
                  disabled={archiving || !cutoffDate}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Archive by Date
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleArchiveAttendance(true)}
                  disabled={archiving}
                  className="flex-1"
                >
                  Archive All
                </Button>
              </div>
              
              <Button variant="ghost" onClick={() => setShowArchiveAttendance(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Archive Registrations Dialog */}
        <Dialog open={showArchiveRegistrations} onOpenChange={setShowArchiveRegistrations}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="text-orange-600" />
                Archive Registrations
              </DialogTitle>
              <DialogDescription>
                Move user enrollments and optionally user accounts to archive storage.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="registrations_cutoff">Archive registrations before this date:</Label>
                <Input
                  id="registrations_cutoff"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                  data-testid="registrations-cutoff-date"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="include_users"
                  checked={includeUsers}
                  onChange={(e) => setIncludeUsers(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="include-users-checkbox"
                />
                <Label htmlFor="include_users" className="text-sm">
                  Also archive user accounts (removes login ability)
                </Label>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> Archiving enrollments removes users from schools. 
                  Archiving user accounts will prevent those users from logging in.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => handleArchiveRegistrations(false)}
                  disabled={archiving || !cutoffDate}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  data-testid="archive-registrations-by-date-btn"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Archive by Date
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleArchiveRegistrations(true)}
                  disabled={archiving}
                  className="flex-1"
                  data-testid="archive-all-registrations-btn"
                >
                  Archive All
                </Button>
              </div>
              
              <Button variant="ghost" onClick={() => setShowArchiveRegistrations(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Clear All Data Dialog */}
        <Dialog open={showClearAll} onOpenChange={setShowClearAll}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle />
                Archive & Clear All Data
              </DialogTitle>
              <DialogDescription>
                This will archive ALL exam results and attendance data, then clear the active database for a fresh start.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>Warning:</strong> This will archive and remove:
                </p>
                <ul className="text-sm text-orange-700 mt-2 list-disc list-inside">
                  <li>{summary?.current_data?.exam_results || 0} exam results</li>
                  <li>{summary?.current_data?.attendance_sessions || 0} attendance sessions</li>
                  <li>{summary?.current_data?.attendance_records || 0} attendance records</li>
                </ul>
                <p className="text-sm text-orange-800 mt-2">
                  Data will be moved to archive and can still be viewed, but will not appear in active reports.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setShowClearAll(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleClearAll}
                  disabled={archiving}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Yes, Archive & Clear All
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Permanent Delete Dialog */}
        <Dialog open={showPermanentDelete} onOpenChange={setShowPermanentDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 />
                Permanently Delete Archived Data
              </DialogTitle>
              <DialogDescription>
                This action CANNOT be undone. All archived data will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Permanently deleting:</strong>
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>{summary?.archived_data?.exam_results || 0} archived exam results</li>
                  <li>{summary?.archived_data?.attendance_sessions || 0} archived attendance sessions</li>
                  <li>{summary?.archived_data?.attendance_records || 0} archived attendance records</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setShowPermanentDelete(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePermanentDelete}
                  disabled={archiving}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Yes, Permanently Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminProtected>
  );
};

export default ArchiveManagementPage;
